import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const PortfolioContainer = styled.div`
  min-height: 100vh;
  padding: 8rem 2rem 2rem;
  background: #0a0a0a;
`;

const Title = styled.h2`
  font-size: 3rem;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(45deg, #ffffff, #888888);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProjectCard = styled(motion.div)`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 16/9;
  cursor: pointer;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const ProjectOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  opacity: 0;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: white;
`;

const ProjectDescription = styled.p`
  color: #888;
  text-align: center;
  font-size: 0.9rem;
`;

const Portfolio = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const projectRefs = useRef<(HTMLDivElement | null)[]>([]);

  const projects = [
    {
      title: "Media Vault",
      description: "A full-stack Media library application",
      image: "/images/mediavault.png",
      link: "https://mediavault.portfolio.tharuka.net/",
    },
    {
      title: "3D Guitar Customizer",
      description: "3D Guitar Customizer using Three.js and React",
      image: "/images/customguitar.png",
      link: "https://customguitar.portfolio.tharuka.net/",
    },
    {
      title: "AI Recipe Extractor",
      description: "Flutter app for extracting recipes from any website",
      image: "/images/recipebook.png",
      link: "https://recipebook.portfolio.tharuka.net",
    },
    {
      title: "Countdown Quiz Game",
      description:
        'Online countdown quiz game (Based on the TV Gameshow "Countdown")',
      image: "/images/countdown.png",
      link: "https://countdown.portfolio.tharuka.net/",
    },
    {
      title: "CampSite",
      description: "Campground Review Site",
      image: "/images/campsite.png",
      link: "https://campsite-scoy.onrender.com/",
    },
  ];

  useEffect(() => {
    // Animate title
    if (titleRef.current) {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }

    // Animate project cards
    projectRefs.current.forEach((card, index) => {
      if (card) {
        gsap.fromTo(
          card,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const addToProjectRefs = (el: HTMLDivElement | null, index: number) => {
    projectRefs.current[index] = el;
  };

  return (
    <PortfolioContainer>
      <Title ref={titleRef}>My Projects</Title>
      <ProjectGrid>
        {projects.map((project, index) => (
          <ProjectCard
            key={project.title}
            ref={(el) => addToProjectRefs(el, index)}
            whileHover={{ scale: 1.05 }}
            onClick={() => window.open(project.link, "_blank")}
          >
            <ProjectImage src={project.image} alt={project.title} />
            <ProjectOverlay
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
            </ProjectOverlay>
          </ProjectCard>
        ))}
      </ProjectGrid>
    </PortfolioContainer>
  );
};

export default Portfolio;
